import type { ChartBar } from "@/services/adapters/chart-adapter";

export type PnfMethod = "hilo" | "close";
export type PnfTargetMode = "conservative" | "neutral" | "aggressive";
export type PnfBoxMode = "atr" | "manual" | "percent";

export interface PnfColumn {
  type: "X" | "O";
  top: number;
  bottom: number;
  startTime: string;
  endTime: string;
}

export interface PnfTier {
  label: string;
  low: number;
  high: number;
  width: number;
  text: string;
}

export interface PnfTargetResult {
  signal: string;
  targetText: string;
  explain: string;
  tiers: Record<PnfTargetMode, PnfTier>;
  mode: PnfTargetMode;
}

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

export function inferTickByPrice(price: number) {
  if (price >= 500) return 0.5;
  if (price >= 100) return 0.1;
  if (price >= 20) return 0.05;
  if (price >= 5) return 0.01;
  return 0.005;
}

function roundToTick(value: number, tick: number) {
  if (!Number.isFinite(value) || !Number.isFinite(tick) || tick <= 0) {
    return value;
  }
  return Math.max(tick, Math.round(value / tick) * tick);
}

export function computeAtrBoxSize({
  lastClose,
  latestAtr,
}: {
  lastClose: number;
  latestAtr: number | null;
}) {
  const tick = inferTickByPrice(lastClose);
  const atr = Number.isFinite(latestAtr) ? Number(latestAtr) : lastClose * 0.01;
  return roundToTick(Math.max(tick, atr), tick);
}

export function computePercentBoxSize({
  lastClose,
  percent,
}: {
  lastClose: number;
  percent: number;
}) {
  const tick = inferTickByPrice(lastClose);
  const normalized = Math.max(0.01, Number(percent) || 1);
  const raw = (lastClose * normalized) / 100;
  return roundToTick(Math.max(tick, raw), tick);
}

export function computeAutoBoxSize({
  lastClose,
  latestAtr,
}: {
  lastClose: number;
  latestAtr: number | null;
}) {
  const tick = inferTickByPrice(lastClose);
  const pctBased = lastClose * 0.01;
  const atrBased = Number.isFinite(latestAtr) ? (latestAtr as number) * 0.5 : 0;
  const raw = Math.max(tick, pctBased, atrBased);
  return roundToTick(raw, tick);
}

export function buildPointFigureColumns(
  bars: ChartBar[],
  boxSize: number,
  reversal: number,
  method: PnfMethod,
): PnfColumn[] {
  if (!bars.length || !Number.isFinite(boxSize) || boxSize <= 0) {
    return [];
  }
  const columns: PnfColumn[] = [];
  let anchor = bars[0].close;
  let current: PnfColumn | null = null;

  const hi = (bar: ChartBar) => (method === "close" ? bar.close : bar.high);
  const lo = (bar: ChartBar) => (method === "close" ? bar.close : bar.low);

  for (let i = 1; i < bars.length; i += 1) {
    const bar = bars[i];
    const high = hi(bar);
    const low = lo(bar);
    if (!Number.isFinite(high) || !Number.isFinite(low)) {
      continue;
    }

    if (!current) {
      if (high >= anchor + boxSize) {
        const top = anchor + Math.floor((high - anchor) / boxSize) * boxSize;
        current = { type: "X", top, bottom: anchor, startTime: bar.time, endTime: bar.time };
        columns.push(current);
      } else if (low <= anchor - boxSize) {
        const bottom = anchor - Math.floor((anchor - low) / boxSize) * boxSize;
        current = { type: "O", top: anchor, bottom, startTime: bar.time, endTime: bar.time };
        columns.push(current);
      }
      continue;
    }

    if (current.type === "X") {
      if (high >= current.top + boxSize) {
        const addBoxes = Math.floor((high - current.top) / boxSize);
        current.top += addBoxes * boxSize;
        current.endTime = bar.time;
      } else if (low <= current.top - reversal * boxSize) {
        const newTop: number = current.top - boxSize;
        const bottom: number = current.top - Math.floor((current.top - low) / boxSize) * boxSize;
        current = { type: "O", top: newTop, bottom, startTime: bar.time, endTime: bar.time };
        columns.push(current);
      } else {
        current.endTime = bar.time;
      }
    } else if (low <= current.bottom - boxSize) {
      const addBoxes = Math.floor((current.bottom - low) / boxSize);
      current.bottom -= addBoxes * boxSize;
      current.endTime = bar.time;
    } else if (high >= current.bottom + reversal * boxSize) {
      const newBottom: number = current.bottom + boxSize;
      const top: number = current.bottom + Math.floor((high - current.bottom) / boxSize) * boxSize;
      current = { type: "X", top, bottom: newBottom, startTime: bar.time, endTime: bar.time };
      columns.push(current);
    } else {
      current.endTime = bar.time;
    }
  }

  return columns.filter((item) => Number.isFinite(item.top) && Number.isFinite(item.bottom) && item.top > item.bottom);
}

function countBoxes(column: PnfColumn, boxSize: number) {
  return Math.max(0, Math.round((column.top - column.bottom) / boxSize));
}

function formatPrice(value: number, boxSize: number) {
  if (!Number.isFinite(value)) {
    return "--";
  }
  const absBox = Math.abs(boxSize);
  const digits = absBox < 0.1 ? 3 : absBox < 1 ? 2 : 1;
  return value.toFixed(digits);
}

export function calcPnfTarget(
  columns: PnfColumn[],
  boxSize: number,
  reversal: number,
  mode: PnfTargetMode,
): PnfTargetResult {
  if (!columns.length) {
    return {
      signal: "无效",
      targetText: "--",
      explain: "列数不足，无法计算。",
      tiers: {
        conservative: { label: "保守", low: 0, high: 0, width: 0, text: "--" },
        neutral: { label: "中性", low: 0, high: 0, width: 0, text: "--" },
        aggressive: { label: "激进", low: 0, high: 0, width: 0, text: "--" },
      },
      mode: "neutral",
    };
  }

  const last = columns[columns.length - 1];
  const before = columns.slice(0, -1);
  const prevXTop = Math.max(...before.filter((item) => item.type === "X").map((item) => item.top), -Infinity);
  const prevOLow = Math.min(...before.filter((item) => item.type === "O").map((item) => item.bottom), Infinity);

  let signal = "中性";
  if (last.type === "X") {
    signal = last.top > prevXTop ? "看涨突破" : "上攻测试";
  } else {
    signal = last.bottom < prevOLow ? "看跌跌破" : "下压测试";
  }

  const boxes = Math.max(1, countBoxes(last, boxSize));
  const tierDefs: Array<{ key: PnfTargetMode; label: string; width: number; vMul: number }> = [
    { key: "conservative", label: "保守", width: Math.min(5, columns.length), vMul: 0.8 },
    { key: "neutral", label: "中性", width: Math.min(9, columns.length), vMul: 1.0 },
    { key: "aggressive", label: "激进", width: Math.min(13, columns.length), vMul: 1.25 },
  ];
  const tiers = {
    conservative: { label: "保守", low: 0, high: 0, width: 0, text: "--" },
    neutral: { label: "中性", low: 0, high: 0, width: 0, text: "--" },
    aggressive: { label: "激进", low: 0, high: 0, width: 0, text: "--" },
  } as Record<PnfTargetMode, PnfTier>;

  for (const cfg of tierDefs) {
    const baseCols = columns.slice(-cfg.width);
    const horizontalCount = cfg.width * reversal * boxSize;
    const horizBaseBull = Math.min(...baseCols.map((item) => item.bottom));
    const horizBaseBear = Math.max(...baseCols.map((item) => item.top));
    const horizTarget = last.type === "X" ? horizBaseBull + horizontalCount : horizBaseBear - horizontalCount;

    const verticalCount = boxes * reversal * boxSize * cfg.vMul;
    const vertTarget = last.type === "X" ? last.bottom + verticalCount : last.top - verticalCount;

    const mid = (horizTarget + vertTarget) / 2;
    const band = Math.max(Math.abs(horizTarget - vertTarget) / 2, boxSize * reversal * 0.5);
    const low = mid - band;
    const high = mid + band;
    tiers[cfg.key] = {
      label: cfg.label,
      low,
      high,
      width: cfg.width,
      text: `${formatPrice(low, boxSize)} ~ ${formatPrice(high, boxSize)}`,
    };
  }

  const normalizedMode = (["conservative", "neutral", "aggressive"] as PnfTargetMode[]).includes(mode) ? mode : "neutral";
  const selected = tiers[normalizedMode] || tiers.neutral;
  return {
    signal,
    targetText: selected.text,
    explain: `水平计数(最近${selected.width}列) + 垂直计数(${boxes}格) 组合；当前档位：${selected.label}。`,
    tiers,
    mode: normalizedMode,
  };
}

export function clampPnfReversal(value: number) {
  return clamp(Math.round(Number(value) || 3), 1, 5);
}
